import { StylishConfirmDialog } from 'components/DesignSystems/New/StylishConfirmDialog';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { useGroupGuid } from 'components/IAP/hooks/commonHooks';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';
import { useUpdateMemberActivation } from 'components/IncidentConfig/hooks/useUpdateMemberActivation';
import IncidentNonActiveConfirmDialog from 'components/IncidentConfig/IncidentNonActiveConfirmDialog';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { getInitials } from 'helpers/intials';
import {
  useMarkIncidentUserActive,
  useRemoveMemberFromPositionInRoster,
} from 'hooks/incident/useRoster';
import {
  useCreateIncidentMemberSession,
  useGetLatestIncidentWorkLogForUser,
  useUpdateIncidentMemberSession,
  WORK_LOG_ACTIONS,
} from 'hooks/incident/useWorkLog';
import React from 'react';
import { useCallback, useMemo } from 'react';

const PositionAssignee: React.FC<{
  incidentId: string;
  positionId: string;
  positionMember: RosterEntry;
}> = ({ incidentId, positionId, positionMember }) => {
  const groupGuid = useGroupGuid();
  const { data: latestIncidentWorkLog } = useGetLatestIncidentWorkLogForUser(
    incidentId,
    positionMember.userId
  );
  const createSession = useCreateIncidentMemberSession();
  const updateSession = useUpdateIncidentMemberSession();
  const checkInMember = useMarkIncidentUserActive(incidentId, groupGuid);
  const removeMember = useRemoveMemberFromPositionInRoster(
    incidentId,
    groupGuid
  );
  const updateMemberActivation = useUpdateMemberActivation();

  const [showNonActiveMemberDialog, setShowNonActiveMemberDialog] =
    React.useState(false);
  const [showDeleteConfirmDialog, setShowDeleteConfirmDialog] =
    React.useState(false);

  const workLogId = React.useMemo(() => {
    return latestIncidentWorkLog?.workLogId || null;
  }, [latestIncidentWorkLog]);

  const latestSession = useMemo(() => {
    if (!latestIncidentWorkLog) {
      return null;
    }

    // useGetLatestIncidentWorkLogForUser returns only one session, the most recent
    return latestIncidentWorkLog.sessions?.[0] || null;
  }, [latestIncidentWorkLog]);

  const availableActions = useMemo(() => {
    return latestIncidentWorkLog?.availableActions || [];
  }, [latestIncidentWorkLog]);

  const handleCheckIn = useCallback(() => {
    if (positionMember.active && positionMember.activeTime) {
      return;
    } else {
      checkInMember.mutate({
        user_guid: positionMember.userId,
        incident_member_id: positionMember.incidentMemberId,
      });
    }
  }, [checkInMember, positionMember]);

  const handleDemobilize = useCallback(() => {
    if (!positionMember.active || !positionMember.activeTime) {
      return;
    } else {
      setShowNonActiveMemberDialog(true);
    }
  }, [positionMember]);

  const handleSignIn = useCallback(() => {
    if (!workLogId) {
      return;
    }

    createSession.mutate({
      incidentId,
      userGuid: positionMember.userId,
      workLogId,
      signInTime: new Date().toISOString(),
    });
  }, [createSession, incidentId, positionMember.userId, workLogId]);

  const handleSignOut = useCallback(() => {
    if (!workLogId || !latestSession) {
      return;
    }

    updateSession.mutate({
      incidentId,
      userGuid: positionMember.userId,
      workLogId,
      sessionId: latestSession?.sessionId || '',
      signInTime: latestSession?.signInTime || '',
      signOutTime: new Date().toISOString(),
    });
  }, [
    updateSession,
    incidentId,
    positionMember.userId,
    workLogId,
    latestSession,
  ]);

  const handleActivationUpdate = useCallback(
    (isActivated: boolean) => {
      updateMemberActivation.mutate({
        /* eslint-disable camelcase */
        incident_id: incidentId,
        member_user_guid: positionMember.userId,
        is_activated: isActivated,
        decline_note: isActivated ? undefined : 'Declined activation',
        /* eslint-enable camelcase */
      });
    },
    [updateMemberActivation, incidentId, positionMember.userId]
  );

  return (
    <div className="d-flex flex-row justify-content-between align-items-center width-100">
      <div className="d-flex flex-row justify-content-left align-items-center">
        <div
          className="rounded-circle avatar-initials sml team-card__members__item__avatar"
          style={{ marginRight: '1rem' }}
        >
          {getInitials(
            positionMember.profileSettings?.userName ||
              positionMember.emailAddress ||
              ''
          )}
        </div>
        <div className="d-flex flex-column">
          <span>{positionMember.userName || '[No name recorded]'}</span>
          <span className="text-muted" style={{ fontSize: '0.8em' }}>
            {positionMember.emailAddress}
          </span>
        </div>
      </div>
      <div className="d-flex flex-row justify-content-right align-items-top">
        <div
          className="d-flex flex-column align-items-top justify-content-start"
          style={{ width: '7rem' }}
        >
          <span className="mb-1" style={{ fontSize: '0.8em' }}>
            ACTIVATION
          </span>

          <div className="d-flex flex-column">
            {positionMember.activatedTimestamp ? (
              <span className="text-success mb-2">Active</span>
            ) : positionMember.declinedTimestamp ? (
              <span className="text-danger mb-2">Declined</span>
            ) : (
              <span className="text-warning mb-2">Pending</span>
            )}
            {(availableActions.includes(WORK_LOG_ACTIONS.activate) ||
              availableActions.includes(WORK_LOG_ACTIONS.decline)) && (
              <div>
                <StylishNewButton
                  className="button--sml mb-1"
                  style={{ width: '100%' }}
                  onClick={() => handleActivationUpdate(true)}
                  disabled={
                    !availableActions.includes(WORK_LOG_ACTIONS.activate)
                  }
                >
                  Activate
                </StylishNewButton>
                <StylishNewButton
                  className="button--sml button--secondary"
                  style={{ width: '100%' }}
                  onClick={() => handleActivationUpdate(false)}
                  disabled={
                    !availableActions.includes(WORK_LOG_ACTIONS.decline)
                  }
                >
                  Decline
                </StylishNewButton>
              </div>
            )}
          </div>
        </div>
        <div
          className="d-flex flex-column"
          style={{ marginLeft: '1rem', width: '7rem' }}
        >
          <span className="mb-1" style={{ fontSize: '0.8em' }}>
            CHECK-IN
          </span>
          {positionMember.active && positionMember.activeTime ? (
            <div className="d-flex flex-column">
              <span className="text-success mb-2">Checked-In</span>
              <StylishNewButton
                className="button--sml"
                onClick={handleDemobilize}
                disabled={!availableActions.includes(WORK_LOG_ACTIONS.checkOut)}
              >
                Demobilize
              </StylishNewButton>
            </div>
          ) : (
            <div className="d-flex flex-column">
              <span className="text-warning mb-2">Demobilized</span>
              <StylishNewButton
                className="button--sml"
                onClick={handleCheckIn}
                disabled={!availableActions.includes(WORK_LOG_ACTIONS.checkIn)}
              >
                Check In
              </StylishNewButton>
            </div>
          )}
        </div>
        <div
          className="d-flex flex-column"
          style={{ marginLeft: '1rem', width: '7rem' }}
        >
          <span className="mb-1" style={{ fontSize: '0.8em' }}>
            SIGN-IN
          </span>
          {latestSession?.signInTime && !latestSession.signOutTime ? (
            <div className="d-flex flex-column">
              <span className="text-success mb-2">Signed In</span>
              <StylishNewButton
                className="button--sml"
                onClick={handleSignOut}
                disabled={!availableActions.includes(WORK_LOG_ACTIONS.signOut)}
              >
                Sign Out
              </StylishNewButton>
            </div>
          ) : (
            <div className="d-flex flex-column">
              <span className="text-warning mb-2">Signed Out</span>
              <StylishNewButton
                className="button--sml"
                onClick={handleSignIn}
                disabled={!availableActions.includes(WORK_LOG_ACTIONS.signIn)}
              >
                Sign In
              </StylishNewButton>
            </div>
          )}
        </div>
        <div className="d-flex flex-column justify-content-center">
          <StylishNewButton
            className="button--icon"
            style={{ marginLeft: '1rem', padding: '0', border: 'none' }}
            onClick={() => setShowDeleteConfirmDialog(true)}
          >
            <SharedIcon
              iconName="delete"
              size="sm"
              color="light"
              title={'Remove user from position'}
              aria-label={'Remove user from position'}
            />
          </StylishNewButton>
        </div>
      </div>

      {showNonActiveMemberDialog && (
        <IncidentNonActiveConfirmDialog
          show={showNonActiveMemberDialog}
          onClose={() => setShowNonActiveMemberDialog(false)}
          active_time={positionMember.activeTime}
          user_guid={positionMember.userId}
          incident_member_id={positionMember.incidentMemberId}
        />
      )}

      {showDeleteConfirmDialog && (
        <StylishConfirmDialog
          show={showDeleteConfirmDialog}
          dialogTitle={'Remove Member Confirmation'}
          dialogContent={`Are you sure you want to remove ${positionMember.userName} from this position? This action cannot be undone.`}
          dialogButtonText={'Remove from Position'}
          onClose={() => setShowDeleteConfirmDialog(false)}
          onConfirm={() => {
            removeMember.mutate({
              incidentMemberId: positionMember.incidentMemberId,
              positionId,
            });
            setShowDeleteConfirmDialog(false);
          }}
        />
      )}
    </div>
  );
};

export default PositionAssignee;
